import { Dispatch, FunctionComponent, SetStateAction, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack } from "@mui/system";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import {
	PreviewSubscriptionChangeResponse,
	useChangeSubscriptionMutation,
	useGetStripeProductsQuery,
	usePreviewSubscriptionChangeMutation,
} from "@/pages/Private/redux/subscription/subscription.api";
import { classNames } from "@/utils/classNames";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

interface ChangeSubscriptionModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	currentPriceId?: string;
}

export const ChangeSubscriptionModal: FunctionComponent<ChangeSubscriptionModalProps> = ({
	isOpen,
	setIsOpen,
	currentPriceId,
}) => {
	const { t } = useTranslation();
	const { data: stripeProducts } = useGetStripeProductsQuery();
	const { user } = useAppSelector(authSelector);
	const allowInstallments = user?.company?.allowInstallments === true;

	type PaymentInterval = "month" | "quarter" | "year" | "semi-annual";

	const [previewChange, { isLoading: isLoadingPreview, error: previewError }] =
		usePreviewSubscriptionChangeMutation();
	const [changeSubscription, { isLoading: isChanging, error: changeError }] =
		useChangeSubscriptionMutation();

	const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
	const [preview, setPreview] = useState<PreviewSubscriptionChangeResponse | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [step, setStep] = useState<"select" | "confirm">("select");
	const [paymentInterval, setPaymentInterval] = useState<PaymentInterval>("month");

	const getMatchingPrice = (product: {
		prices: Array<{
			interval: string;
			isSemiAnnual?: boolean;
			isQuarterly?: boolean;
			id: string;
			monthlyPrice: number;
			annualPrice: number;
			costsPerCredit: number;
			price: number;
		}>;
	}) => {
		if (paymentInterval === "semi-annual") {
			return product.prices.find((price) => price.isSemiAnnual === true);
		}

		if (paymentInterval === "quarter") {
			return product.prices.find((price) => price.isQuarterly === true);
		}

		return product.prices.find(
			(price) => price.interval === paymentInterval && !price.isSemiAnnual && !price.isQuarterly
		);
	};

	const intervalLabel = useMemo(() => {
		if (paymentInterval === "semi-annual" || paymentInterval === "quarter") {
			return t("usage.subscription.intervalLabels.year");
		}

		if (paymentInterval === "year") {
			return t("usage.subscription.intervalLabels.year");
		}

		return t("usage.subscription.intervalLabels.month");
	}, [paymentInterval, t]);

	// Available plans filtered by selected interval (like SelectSubscription.tsx)
	const availablePlans = useMemo(() => {
		if (!stripeProducts) {
			return [];
		}

		return stripeProducts
			.filter((product) => !!getMatchingPrice(product))
			.map((product) => {
				const priceObject = getMatchingPrice(product);

				return {
					productId: product.id,
					productName: product.name,
					credits: paymentInterval === "month" ? product.credits : product.credits * 12,
					interval: intervalLabel,
					priceAmount: priceObject?.monthlyPrice || 0,
					priceId: priceObject?.id || "",
					annualPrice: priceObject?.annualPrice || 0,
					costsPerCredit: priceObject?.costsPerCredit || 0,
					price: priceObject?.price || 0,
				};
			});
	}, [stripeProducts, paymentInterval, intervalLabel]);

	const handleSelectPlan = async (priceId: string) => {
		if (priceId === currentPriceId) {
			return;
		}

		setSelectedPriceId(priceId);

		try {
			const result = await previewChange({ priceId }).unwrap();

			setPreview(result);
			setStep("confirm");
		} catch (err) {
			console.error("Preview failed:", err);
		}
	};

	const handleConfirmChange = async () => {
		if (!selectedPriceId) {
			return;
		}

		try {
			const result = await changeSubscription({ priceId: selectedPriceId }).unwrap();

			setSuccessMessage(result.message);

			setTimeout(() => {
				setSuccessMessage(null);
				setIsOpen(false);
				resetModal();
			}, 3000);
		} catch (err) {
			console.error("Change failed:", err);
		}
	};

	const resetModal = () => {
		setSelectedPriceId(null);
		setPreview(null);
		setStep("select");
		setSuccessMessage(null);
	};

	const handleClose = () => {
		resetModal();
		setIsOpen(false);
	};

	const handleBack = () => {
		setStep("select");
		setPreview(null);
	};

	const formatPrice = (amount: number) => {
		return new Intl.NumberFormat("de-DE", {
			style: "currency",
			currency: "EUR",
		}).format(amount);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("de-DE", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		});
	};

	const getIntervalLabel = (interval: string) => {
		switch (interval) {
			case "half_year":
				return t("usage.subscription.intervalLabels.halfYear");
			case "quarter":
				return t("usage.subscription.intervalLabels.quarter");
			case "year":
				return t("usage.subscription.intervalLabels.year");
			default:
				return t("usage.subscription.intervalLabels.month");
		}
	};

	const getCreditsIntervalLabel = (interval: string) => {
		return interval === "month"
			? t("usage.subscription.intervalLabels.month")
			: t("usage.subscription.intervalLabels.year");
	};

	const getDisplayCredits = (credits: number, interval: string) => {
		return interval === "month" ? credits : credits * 12;
	};

	const getChangeTypeLabel = (changeType: string) => {
		switch (changeType) {
			case "upgrade":
				return t("usage.subscription.change.changeTypes.upgrade");
			case "downgrade":
				return t("usage.subscription.change.changeTypes.downgrade");
			case "interval_change":
				return t("usage.subscription.change.changeTypes.interval_change");
			case "no_change":
				return t("usage.subscription.change.changeTypes.no_change");
			default:
				return changeType;
		}
	};

	const getSubmitButtonText = () => {
		if (!preview) {
			return t("basics.confirm");
		}

		if (preview.changeType === "downgrade") {
			return t("usage.subscription.change.scheduleDowngrade");
		}

		if (!preview.immediate) {
			return t("usage.subscription.change.scheduleChange");
		}

		return t("usage.subscription.change.confirmChange");
	};

	const formErrors: ERROR_TYPE[] = [];
	const error = previewError || changeError;

	// Render plan selection
	const renderPlanSelection = () => (
		<Stack spacing={3}>
			{/* Interval Toggle */}
			<div className="flex flex-row gap-2 items-center justify-center">
				<button
					className={classNames(
						"px-3 py-1.5 rounded-lg text-sm transition-colors",
						paymentInterval === "month"
							? "bg-blue-action text-white font-bold"
							: "text-text-meta font-normal hover:bg-background-light"
					)}
					type="button"
					onClick={() => setPaymentInterval("month")}
				>
					{t("usage.subscription.intervals.monthly")}
				</button>
				{allowInstallments && (
					<button
						className={classNames(
							"px-3 py-1.5 rounded-lg text-sm transition-colors",
							paymentInterval === "quarter"
								? "bg-blue-action text-white font-bold"
								: "text-text-meta font-normal hover:bg-background-light"
						)}
						type="button"
						onClick={() => setPaymentInterval("quarter")}
					>
						{t("usage.subscription.intervals.quarterly")}
					</button>
				)}
				<button
					className={classNames(
						"px-3 py-1.5 rounded-lg text-sm transition-colors",
						paymentInterval === "year"
							? "bg-blue-action text-white font-bold"
							: "text-text-meta font-normal hover:bg-background-light"
					)}
					type="button"
					onClick={() => setPaymentInterval("year")}
				>
					{t("usage.subscription.intervals.annually")}
				</button>
				{allowInstallments && (
					<button
						className={classNames(
							"px-3 py-1.5 rounded-lg text-sm transition-colors",
							paymentInterval === "semi-annual"
								? "bg-blue-action text-white font-bold"
								: "text-text-meta font-normal hover:bg-background-light"
						)}
						type="button"
						onClick={() => setPaymentInterval("semi-annual")}
					>
						{t("usage.subscription.intervals.semiAnnual")}
					</button>
				)}
			</div>
			<div className="text-text-meta text-sm text-center">
				{paymentInterval === "year" && t("usage.subscription.intervalSubcopy.year")}
				{paymentInterval === "quarter" && t("usage.subscription.intervalSubcopy.quarter")}
				{paymentInterval === "semi-annual" && t("usage.subscription.intervalSubcopy.semiAnnual")}
				{paymentInterval === "month" && t("usage.subscription.intervalSubcopy.month")}
			</div>

			<p className="text-sm text-text-meta">{t("usage.subscription.change.selectNewPlan")}</p>
			<div className="flex flex-col gap-2 max-h-[350px] overflow-y-auto">
				{availablePlans.map((plan) => {
					const isCurrentPlan = plan.priceId === currentPriceId;

					return (
						<button
							key={plan.priceId}
							className={`p-4 border rounded-lg text-left transition-colors ${
								isCurrentPlan
									? "border-gray-300 bg-gray-50 cursor-not-allowed"
									: "border-gray-200 hover:border-brand-dark hover:bg-gray-50 cursor-pointer"
							}`}
							disabled={isCurrentPlan || isLoadingPreview}
							type="button"
							onClick={() => handleSelectPlan(plan.priceId)}
						>
							<div
								className={classNames(
									"flex justify-between items-center",
									isLoadingPreview && "opacity-50 pointer-events-none"
								)}
							>
								<div>
									<span className="font-semibold">{plan.productName}</span>
									<span className="text-sm text-text-meta ml-2">
										{t("usage.subscription.change.creditsPerInterval", {
											credits: plan.credits.toLocaleString("de-DE"),
											interval: plan.interval,
										})}
									</span>
								</div>
								<div className="text-right">
									{paymentInterval === "semi-annual" ? (
										<span className="font-semibold">
											{t("usage.subscription.change.priceFor6Months", {
												price: formatPrice(plan.price / 100),
											})}
										</span>
									) : paymentInterval === "quarter" ? (
										<span className="font-semibold">
											{t("usage.subscription.change.priceFor3Months", {
												price: formatPrice(plan.price / 100),
											})}
										</span>
									) : plan.interval === "month" ? (
										<span className="font-semibold">
											{t("usage.subscription.change.pricePerMonth", {
												price: formatPrice(plan.priceAmount),
											})}
										</span>
									) : (
										<span className="font-semibold">
											{t("usage.subscription.change.pricePerYear", {
												price: formatPrice(plan.annualPrice / 100),
											})}
										</span>
									)}
									{isCurrentPlan && (
										<span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">
											{t("usage.subscription.change.current")}
										</span>
									)}
								</div>
							</div>
						</button>
					);
				})}
			</div>
		</Stack>
	);

	// Render confirmation view
	const renderConfirmation = () => {
		if (!preview) {
			return null;
		}

		return (
			<Stack spacing={3}>
				{!preview.allowed && (
					<div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
						{preview.notAllowedReason}
					</div>
				)}

				{preview.allowed && (
					<>
						{/* Current Plan */}
						<div className="rounded-lg border border-gray-200 p-4">
							<div className="text-xs text-text-meta mb-1">
								{t("usage.subscription.change.currentPlan")}
							</div>
							<div className="font-semibold">
								{preview.currentSubscription.productName}{" "}
								{t("usage.subscription.change.creditsPerInterval", {
									credits: getDisplayCredits(
										preview.currentSubscription.credits,
										preview.currentSubscription.interval
									).toLocaleString("de-DE"),
									interval: getCreditsIntervalLabel(preview.currentSubscription.interval),
								})}
							</div>
							<div className="text-sm text-text-meta">
								{t("usage.subscription.change.pricePer", {
									price: formatPrice(preview.currentSubscription.priceAmount),
									interval: getIntervalLabel(preview.currentSubscription.interval),
								})}
							</div>
						</div>

						{/* New Plan */}
						<div className="rounded-lg text-white border border-brand-dark bg-brand-light p-4">
							<div className="text-xs text-white mb-1">
								{t("usage.subscription.change.newPlan")}
							</div>
							<div className="font-semibold">
								{preview.newSubscription.productName}{" "}
								{t("usage.subscription.change.creditsPerInterval", {
									credits: getDisplayCredits(
										preview.newSubscription.credits,
										preview.newSubscription.interval
									).toLocaleString("de-DE"),
									interval: getCreditsIntervalLabel(preview.newSubscription.interval),
								})}
							</div>
							<div className="text-sm text-white">
								{t("usage.subscription.change.pricePer", {
									price: formatPrice(preview.newSubscription.priceAmount),
									interval: getIntervalLabel(preview.newSubscription.interval),
								})}
							</div>
						</div>

						{/* Change Details */}
						<div className="rounded-lg bg-gray-50 p-4">
							<div className="flex justify-between mb-2">
								<span className="text-sm text-text-meta">
									{t("usage.subscription.change.changeType")}
								</span>
								<span className="text-sm font-semibold">
									{getChangeTypeLabel(preview.changeType)}
								</span>
							</div>

							{preview.immediate && preview.proratedAmountPreview !== null && (
								<div className="flex justify-between mb-2">
									<span className="text-sm text-text-meta">
										{t("usage.subscription.change.chargedLabel")}
									</span>
									<span className="text-sm font-semibold">
										{t("usage.subscription.change.prorated", {
											amount: formatPrice(preview.proratedAmountPreview),
										})}
									</span>
								</div>
							)}

							<div className="flex justify-between">
								<span className="text-sm text-text-meta">
									{t("usage.subscription.change.effective")}
								</span>
								<span className="text-sm font-semibold">
									{preview.immediate
										? t("usage.subscription.change.immediately")
										: preview.effectiveAt
											? formatDate(preview.effectiveAt)
											: t("usage.subscription.change.endOfCycle")}
								</span>
							</div>

							{!preview.immediate && (
								<div className="mt-3 text-sm text-text-meta">
									{t("usage.subscription.change.keepCurrentPlan")}
								</div>
							)}
						</div>
					</>
				)}
			</Stack>
		);
	};

	return (
		<Modal
			handleClose={handleClose}
			handleSave={step === "confirm" && preview?.allowed ? handleConfirmChange : undefined}
			isLoading={isLoadingPreview || isChanging}
			isOpened={isOpen}
			size="sm"
			submitButtonText={getSubmitButtonText()}
			title={
				step === "select"
					? t("usage.subscription.change.title")
					: t("usage.subscription.change.confirmTitle")
			}
			{...(step === "confirm" && {
				handleClear: handleBack,
				clearButtonText: t("usage.subscription.change.back"),
			})}
		>
			<Stack spacing={2}>
				{successMessage && (
					<div className="rounded-md bg-green-50 p-3 text-sm text-green-700">{successMessage}</div>
				)}

				{!successMessage && (step === "select" ? renderPlanSelection() : renderConfirmation())}

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</Stack>
		</Modal>
	);
};
